import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { H3, H4, H5, H6, Paragraph } from "components/Typography";
import { FlexRow } from "components/flex-box";
import {
  AllPartDetailsType,
  ProductDetailsCustomSpecs,
} from "modules/product-details/types";
import parse from "html-react-parser";

import { toTitleCase, _ } from "common";

// ======================================================
type ProductDescriptionProps = {
  productName: string;
  references?: string[];
  warrantyDuration?: string;
  description?: string;
  productPartDetails?: AllPartDetailsType;
  customSpecs?: ProductDetailsCustomSpecs[];
};
// ======================================================

const ProductDescription: React.FC<ProductDescriptionProps> = ({
  productPartDetails,
  references,
  warrantyDuration,
  description = "",
  customSpecs,
  productName,
}) => {
  const [htmlContent, setHtmlContent] = useState<string>("");
  const [linkContent, setLinkContent] = useState<string>("");

  const specsMapped = useMemo(() => {
    const exception: string[] = ["image_thumbnail_url", "image_url"];
    var output = Object.entries(productPartDetails || [])
      .map(([key, value]) => {
        if (exception?.includes(key)) {
          return;
        }
        return { key, value };
      })
      ?.filter((x) => x && x !== undefined && x !== null && x.value);
    return output;
  }, [productPartDetails]);

  const combinedCustomAndPartsSpecs = useMemo(() => {
    const clonedSpecsMapped = _.cloneDeep(specsMapped || []);
    const clonedCustomSpecs = _.cloneDeep(customSpecs || []);
    const arrangedCustomSpecs = clonedCustomSpecs?.map((x) => ({
      key: x?.title,
      value: x?.value,
    }));
    const mergedArr = clonedSpecsMapped.concat(arrangedCustomSpecs);
    const arrTitleCase = mergedArr?.map((x) => ({
      key: toTitleCase(x?.key || ""),
      value: x?.value,
    }));
    return arrTitleCase;
  }, [specsMapped, customSpecs]);

  //first decode base 64
  useEffect(() => {
    if (description) {
      let decodedDescription = "";
      //for rendering special char
      try {
        decodedDescription = decodeURIComponent(
          escape(window.atob(description))
        );
      } catch (e) {
        console.error("Error decoding description:", e);
        setHtmlContent("");
      }

      // Create a temporary div to hold the HTML string
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = decodedDescription;

      // Append the temporary div to the body to render it
      document.body.appendChild(tempDiv);

      // Find the table element and get its width
      const table = tempDiv.querySelector<HTMLTableElement>("table");

      if (table) {
        const tableWidth = table.offsetWidth;

        // Calculate and update col widths to percentage
        const cols = tempDiv.querySelectorAll<HTMLTableColElement>("col");
        cols.forEach((col) => {
          const width = col.style.width.replace("px", "");
          if (width) {
            const percentageWidth = (parseInt(width, 10) / tableWidth) * 100;
            col.style.width = `${percentageWidth}%`;
          }
        });

        // Get the updated HTML string with percentage widths
        const updatedHtmlString = tempDiv.innerHTML;

        const modifiedDescription = updatedHtmlString.replace(
          /minWidth:[^;"]*;?/g,
          ""
        );

        // Clean up: remove the temporary div
        document.body.removeChild(tempDiv);

        // Update the state with the updated HTML string
        setHtmlContent(modifiedDescription);
      } else {
        // Clean up: remove the temporary div if no table is found
        document.body.removeChild(tempDiv);
        //the original product description

        setHtmlContent(decodedDescription);
      }
    } else {
      setHtmlContent("");
    }
  }, [description]);

  //lets fetch the link
  useEffect(() => {
    const fetchData = async () => {
      const response = await fetch(
        `/api/proxy?url=${encodeURIComponent(htmlContent)}`
      );
      const data = await response.json();
      setLinkContent(data);
    };

    fetchData();
  }, [htmlContent]);

  return (
    <Box>
      <H4 mb={1}>{productName}</H4>
      {combinedCustomAndPartsSpecs?.length > 0 ? (
        <Box mt={7}>
          <H3 mb={1}>Specifications:</H3>
          <Box>
            {combinedCustomAndPartsSpecs?.map((x) => {
              return (
                <FlexRow key={x?.key} mb={1} alignItems="center">
                  <H6 color="grey.800">{x?.key}:</H6>
                  <Paragraph ml={1}>{x?.value}</Paragraph>
                </FlexRow>
              );
            })}
          </Box>
        </Box>
      ) : null}
      {warrantyDuration ? (
        <Box mt={7}>
          <H3 mb={1}>Warranty Details:</H3>
          <Box>
            <FlexRow mb={1} alignItems="center">
              <H6 color="grey.800">Duration:</H6>
              <Paragraph ml={1}>{warrantyDuration}</Paragraph>
            </FlexRow>
          </Box>
        </Box>
      ) : null}

      {linkContent && typeof linkContent === "string" ? (
        <Box mt={7}>
          <H3 mb={1}>Product Description</H3>
          <Box>{parse(linkContent)}</Box>
        </Box>
      ) : null}

      {references?.length > 0 ? (
        <Box mt={7}>
          <H3 mb={1}>References:</H3>
          <Box>
            {references?.map((x, i) => {
              return (
                <FlexRow key={i} mb={1} alignItems="center">
                  <H6 color="grey.800">•</H6>
                  <a
                    key={i}
                    href={x}
                    target="_blank"
                    rel="noreferrer noopenner"
                  >
                    <Paragraph
                      sx={{
                        textDecoration: "underline",
                        overflowWrap: "anywhere",
                      }}
                      ml={1}
                    >
                      {x}
                    </Paragraph>
                  </a>
                </FlexRow>
              );
            })}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default ProductDescription;
